// Copyright: IQGeo Limited 2010-2019
import myw from 'myWorld/base/core';
import L from 'leaflet';
import _ from 'underscore';

myw.TileBounds = myw.Class.extend({
    /**
     * @class  The bounds of a tile in pixel space.<br/>
     * @constructs
     * @param  {L.Point} tilePoint  Origin of the tile in tile space
     * @param  {integer} tileSize   Size of the tile in pixels
     */
    initialize(tilePoint, tileSize) {
        const north = tilePoint.y * tileSize;
        const south = north + tileSize;
        const west = tilePoint.x * tileSize;
        const east = west + tileSize;
        this._bounds = L.bounds([west, north], [east, south]);
    },

    /**
     * Return a multi-polygon geometry clipped by the tile bounds.
     * @param {Array<Array<Array<L.Point>>>} multiPolygonCoordinates  The multi-polygon to clip by
     * @return {Array<Array<Array<L.Point>>>} The clipped polygon
     */
    clipByMultiPolygon(multiPolygonCoordinates) {
        const clippedMultiPolygon = [];
        _.each(multiPolygonCoordinates, polygonCoordinates => {
            const clippedPolygon = this.clipByPolygon(polygonCoordinates);
            if (clippedPolygon.length > 0) {
                clippedMultiPolygon.push(clippedPolygon);
            }
        });
        return clippedMultiPolygon;
    },

    /**
     * Return a polygon geometry clipped by the tile bounds.
     * @param {Array<Array<L.Point>>} polygonCoordinates  The polygon to clip by
     * @return {Array<Array<L.Point>>} The clipped polygon
     */
    clipByPolygon(polygonCoordinates) {
        const clipped = [];
        const outerClip = this.clipByLineRing(polygonCoordinates[0]);
        if (outerClip.length > 0) {
            clipped.push(outerClip);
            for (let index = 1; index < polygonCoordinates.length; index++) {
                const innerClip = this.clipByLineRing(polygonCoordinates[index]);
                if (innerClip.length > 0) {
                    if (this._coveredBy(innerClip)) {
                        // Completely inside hole
                        return [];
                    }
                    clipped.push(innerClip);
                }
            }
        }
        return clipped;
    },

    /**
     * Return a ling ring geometry clipped by the tile bounds.
     * @param {Array<L.Point>} lineRingCoordinates  The line ring to clip by
     * @return {Array<L.Point>} The clipped line ring
     */
    clipByLineRing(lineRingCoordinates) {
        return L.PolyUtil.clipPolygon(lineRingCoordinates, this._bounds);
    },

    /**
     * Return the minimum of the tile bounds in pixel space
     * @return {L.Point}
     */
    getMin() {
        return this._bounds.min;
    },

    /**
     * Determine whether the clipped geometry covers the whole of the tile
     * specified by tileBounds.
     * @param {Array<L.Point>} clippedGeometry  The clipped geometry
     * @return {boolean} True if the whole tile is covered, False otherwise
     * clippedGeometry is an unclosed linestring
     * @private
     */
    _coveredBy(clippedGeometry) {
        // We have the whole tile if the clippedGeometry describes the
        // unclosed boundary path of the bounds.
        //
        // We assume that in that case the clipping algorithm gives us:
        //   1. an unclosed path of 4 points
        //   2. the points are distinct
        //   3. the points ordered CW or ACW

        if (clippedGeometry.length != 4) {
            // Assumption 1.
            return false;
        }

        // Check that each coordinate in clippedGeometry is a corner of
        // the tile bounds.

        const north = this._bounds.min.y;
        const south = this._bounds.max.y;
        const west = this._bounds.min.x;
        const east = this._bounds.max.x;

        for (let index = 0; index < 4; index++) {
            const coordinate = clippedGeometry[index];
            if (coordinate.x != west && coordinate.x != east) {
                return false;
            }
            if (coordinate.y != north && coordinate.y != south) {
                return false;
            }
        }

        // All 4 points are corners of the tile bounds. By assumption 2.,
        // we know that we have all four corners of the bounds.
        // By assumption 3., we know that the clippedGeometry describes
        // the unclosed boundary path of the bounds.
        return true;
    }
});

export default myw.TileBounds;
